import { cloneDeep } from 'lodash'

import UserSettingService from 'Services/UserSettingService'

const settingsKey = 'FilterPresets'

export default {
  data () {
    return {
      filterPresets: []
    }
  },

  mounted () {
    this.getFilterPresets(this.$options.name).then(presets => {
      this.filterPresets = presets
    })
  },

  methods: {
    /**
     * Get preset filters
     */
    getPresetFilters () {
      return []
    },

    /**
     * We only need to store the filter 'key' and 'value'
     *
     * @param {Array} filters
     */
    serializeFilter (filters) {
      return filters.map(filter => ({ key: filter.key, value: filter.value }))
    },

    /**
     * Parse the stored preset filters and copy the stored value.
     *
     * @param {Array} filters
     */
    parseFilter (filters) {
      return this.getPresetFilters().reduce((result, item) => {
        if (filters.some(filter => filter.key === item.key)) {
          const clonedFilter = cloneDeep(item)
          clonedFilter.value = filters.find(filter => filter.key === item.key).value

          result.push(clonedFilter)
        }

        return result
      }, [])
    },

    /**
     * Get the stored presets
     *
     * @param {String} key
     */
    getFilterPresets (key) {
      return UserSettingService.getUserSettings(`${key}${settingsKey}`, []).then(result => {
        return result.map(item => ({ ...item, filters: this.parseFilter(item.filters) }))
      })
    },

    /**
     * Update the stored presets
     *
     * @param {String} key
     * @param {Array} presets
     */
    updateFilterPresets (key, presets) {
      return new Promise((resolve, reject) => {
        UserSettingService.saveUserSettings(`${key}${settingsKey}`, presets).then(() => {
          this.getFilterPresets(key).then((presets) => {
            this.filterPresets = presets

            resolve(presets)
          })
        }).catch(error => {
          reject(error)
        })
      })
    },

    /**
     * Add the new preset to the list of other presets and store it.
     *
     * @param {String} key
     * @param {Object} preset
     */
    addFilterPreset (key, preset) {
      const presets = [...this.filterPresets, preset].map(item => ({ ...item, filters: this.serializeFilter(item.filters) }))

      return this.updateFilterPresets(key, presets)
    },

    /**
     * Remove the selected preset by identifier and store it.
     *
     * @param {String} key
     * @param {String} identifier
     */
    removeFilterPreset (key, identifier) {
      const presets = this.filterPresets.filter(item => item.identifier !== identifier).map(item => ({ ...item, filters: this.serializeFilter(item.filters) }))

      return this.updateFilterPresets(key, presets)
    },

    /**
     * Get the preset filters and show the form
     */
    onSavePresetClick () {
      const filters = this.getPresetFilters().filter(filter => filter.hasValue())

      this.$store.dispatch('showModal', {
        component: {
          is: () => import('@/components/Table/Components/Forms/FilterPresetForm.vue'),
          data: {
            filters: filters
          },
          events: {
            save: ({ preset, next }) => {
              this.addFilterPreset(this.$options.name, preset).then(() => {
                this.$store.dispatch('showToastMessage', { title: this.$i18n.t('label.{label}Saved', { label: this.$i18n.t('label.filter') }), icon: ['fas', 'check'], options: { variant: 'success' } })
                next(true)
              }).catch(() => next(false))
            }
          }
        },
        options: {
          title: this.$i18n.t('label.save{label}', { label: this.$i18n.t('label.filter') }),
          size: 'md'
        },
        events: {
          ok: ($event) => this.$eventHub.emit('ok', $event),
          cancel: ($event) => this.$eventHub.emit('cancel', $event)
        },
        actions: [{
          onClick: ({ cancel }) => cancel(),
          options: {
            title: this.$i18n.t('label.cancel'),
            variant: 'link-danger',
            class: 'mr-3'
          }
        }, {
          onClick: ({ ok }) => ok(),
          isDisabled: ({ isValid, isLoading }) => !isValid || isLoading,
          isLoading: ({ isLoading }) => isLoading,
          options: {
            title: this.$i18n.t('label.save'),
            iconPrepend: ['fal', 'save'],
            variant: 'secondary',
            isPill: true
          }
        }]
      })
    },

    /**
     * Apply the selected preset
     *
     * @param {String} identifier
     */
    onApplyPresetClick (identifier) {
      const filters = this.getPresetFilters()
      const preset = this.filterPresets.find(item => item.identifier === identifier)

      filters.forEach(filter => filter.reset())
      preset.filters.forEach(presetFilter => {
        if (filters.some(filter => filter.key === presetFilter.key)) {
          filters.find(filter => filter.key === presetFilter.key).value = presetFilter.value
        }
      })

      this.$store.dispatch('showToastMessage', { title: this.$i18n.t('label.{label}Applied', { label: preset.name }), autoHideDelay: 5000, options: { variant: 'success' } })
    },

    /**
     * Remove the selected filter preset
     *
     * @param {String} identifier
     */
    onRemovePresetClick (identifier) {
      const preset = this.filterPresets.find(item => item.identifier === identifier)

      this.$store.dispatch('showModal', {
        component: {
          is: 'MessageModal',
          data: {
            message: this.$i18n.t('sentence.{label}DeleteConfirmation', { label: `${this.$i18n.t('label.filter').toLowerCase()} - ${preset.name}` })
          }
        },
        options: {
          title: this.$i18n.t('label.delete{label}', { label: this.$i18n.t('label.filter') }),
          size: 'md'
        },
        events: {
          ok: () => this.removeFilterPreset(this.$options.name, identifier).then(() => {
            this.$store.dispatch('showToastMessage', { title: this.$i18n.t('label.{label}Removed', { label: this.$i18n.t('label.filter') }), autoHideDelay: 5000, options: { variant: 'success' } })
          })
        },
        actions: [{
          onClick: ({ cancel }) => cancel(),
          options: {
            title: this.$i18n.t('label.cancel'),
            variant: 'link',
            class: 'mr-3 btn-link--primary'
          }
        }, {
          onClick: ({ ok }) => ok(),
          options: {
            title: this.$i18n.t('label.delete'),
            variant: 'danger',
            isPill: true
          }
        }]
      })
    }
  }
}
